import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Calendar,
  Users,
  FileText,
  Save,
  Clock,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import "../../assets/styles/EditActivity.css";
import { updateActivityRequest } from "../../services/activityService";
import PopupMessage from "../../components/common/PopupMessage";

export default function EditActivity() {
  const navigate = useNavigate();
  const location = useLocation();
  const [popup, setPopup] = useState({ show: false, message: "", type: "" });

  // التأكد من وجود الداتا لتجنب الأخطاء
  const incomingData = location.state?.activity;

  // --- Helpers ---
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
  };

  // --- State ---
  const [formData, setFormData] = useState({
    activityName: "",
    description: "",
    specialInstructions: "",
    capacity: "",
    registrationOpenAt: "",
    registrationCloseAt: "",
    activityStartAt: "",
    activityEndAt: "",
  });

  const [initialData, setInitialData] = useState({});
  const [isChanged, setIsChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // --- Load Data ---
  useEffect(() => {
    if (incomingData) {
      const formattedData = {
        activityName: incomingData.activityName || "",
        description: incomingData.description || "",
        specialInstructions: incomingData.specialInstructions || "",
        capacity: incomingData.capacity || "",
        registrationOpenAt: formatDate(
          incomingData.registration?.openAt || incomingData.registrationOpenAt,
        ),
        registrationCloseAt: formatDate(
          incomingData.registration?.closeAt ||
            incomingData.registrationCloseAt,
        ),
        activityStartAt: formatDate(
          incomingData.activitySchedule?.startAt ||
            incomingData.activityStartAt,
        ),
        activityEndAt: formatDate(
          incomingData.activitySchedule?.endAt || incomingData.activityEndAt,
        ),
      };

      setFormData(formattedData);
      setInitialData(formattedData);
    }
  }, [incomingData]);

  // --- Validation Logic (Wrapped in useCallback) ---
  const validateForm = useCallback(
    (updateState = true) => {
      const newErrors = {};
      const {
        registrationOpenAt,
        registrationCloseAt,
        activityStartAt,
        activityEndAt,
        capacity,
      } = formData;

      // 1. Capacity Check
      if (Number(capacity) <= 0) {
        newErrors.capacity = "Capacity must be greater than 0";
      }

      // 2. Date Logic Checks
      if (
        registrationCloseAt &&
        registrationOpenAt &&
        registrationCloseAt <= registrationOpenAt
      ) {
        newErrors.registrationCloseAt =
          "Closing date must be after opening date.";
      }
      if (
        activityStartAt &&
        registrationCloseAt &&
        activityStartAt <= registrationCloseAt
      ) {
        newErrors.activityStartAt =
          "Activity usually starts after registration closes.";
      }
      if (
        activityEndAt &&
        activityStartAt &&
        activityEndAt <= activityStartAt
      ) {
        newErrors.activityEndAt = "End time must be after start time.";
      }

      if (updateState) setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [formData],
  );

  // --- Change Detection & Real-time Validation ---
  useEffect(() => {
    // 1. Check if data changed
    setIsChanged(JSON.stringify(formData) !== JSON.stringify(initialData));

    // 2. Real-time Validation Clearing (only if errors exist)
    if (Object.keys(errors).length > 0) {
      validateForm(false);
    }
  }, [formData, initialData, errors, validateForm]);

  // --- Handlers ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    if (validateForm(true)) {
      setShowConfirmModal(true);
    }
  };

  // const handleConfirmSave = async () => {
  //   setLoading(true);
  //   try {
  //     // 1. تجهيز البيانات (تحويل الأرقام وضمان الهيكل الصحيح)
  //     const payload = {
  //       ...formData,
  //       capacity: Number(formData.capacity),
  //     };

  //     // 2. نداء الـ API الحقيقي (Uncommented now)
  //     const response = await updateActivityRequest(incomingData._id, payload);

  //     setPopup({
  //       show: true,
  //       message: response.message || "Activity updated successfully!",
  //       type: "success",
  //     });

  //     // 3. نجاح العملية
  //     setLoading(false);
  //     setShowConfirmModal(false);

  //     // العودة للصفحة السابقة وتحديث البيانات
  //     setTimeout(() => {
  //       navigate(-1);
  //     }, 3000);
  //   } catch (error) {
  //     console.error("Update Failed:", error);
  //     setPopup({
  //       show: true,
  //       message: error.message || "something went wrong",
  //       type: "error",
  //     });
  //     setLoading(false);
  //     setShowConfirmModal(false);
  //   }
  // };
  const handleConfirmSave = async () => {
    setLoading(true);
    try {
      // دالة صغيرة بتحول التوقيت من محلي (مصر) لعالمي (UTC) عشان يتخزن صح
      const toUTC = (dateStr) => {
        if (!dateStr) return null;
        return new Date(dateStr).toISOString();
      };

      // 1. تجهيز البيانات
      const payload = {
        ...formData,
        capacity: Number(formData.capacity),
        // هنا السر: بنظبط التوقيت قبل ما يسافر للسيرفر
        registrationOpenAt: toUTC(formData.registrationOpenAt),
        registrationCloseAt: toUTC(formData.registrationCloseAt),
        activityStartAt: toUTC(formData.activityStartAt),
        activityEndAt: toUTC(formData.activityEndAt),
      };

      // 2. نداء الـ API
      const response = await updateActivityRequest(incomingData._id, payload);

      setPopup({
        show: true,
        message: response.message || "Activity updated successfully!",
        type: "success",
      });

      setLoading(false);
      setShowConfirmModal(false);

      setTimeout(() => {
        navigate(-1);
      }, 3000);
    } catch (error) {
      console.error("Update Failed:", error);
      setPopup({
        show: true,
        message: error.message || "something went wrong",
        type: "error",
      });
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  if (!incomingData) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 text-muted">
        <AlertCircle size={48} className="mb-3" />
        <h5>No Activity Data Found</h5>
        <button className="btn btn-primary mt-2" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: "70px" }} className="edit-wrapper">
      <div className="container" style={{ maxWidth: "750px" }}>
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-5">
          <div>
            <h2 className="fw-bold text-dark mb-1">Edit Activity</h2>
            <p className="text-muted small mb-0">
              Update details for{" "}
              <span className="fw-bold text-primary">
                {incomingData.activityName}
              </span>
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="btn btn-light rounded-circle shadow-sm p-2"
            style={{
              width: "45px",
              height: "45px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ArrowLeft size={22} />
          </button>
        </div>

        <form onSubmit={handlePreSubmit}>
          {/* === 1. Essential Info === */}
          <div className="custom-card">
            <div className="card-header-custom d-flex align-items-center gap-2">
              <FileText className="text-primary" size={20} />
              <h6 className="fw-bold m-0 text-dark">Essential Information</h6>
            </div>
            <div className="card-body-custom">
              <div className="mb-4">
                <label className="form-label-custom">Activity Title</label>
                <input
                  type="text"
                  name="activityName"
                  className="input-custom fw-bold"
                  value={formData.activityName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-4">
                <label className="form-label-custom">Description</label>
                <textarea
                  name="description"
                  rows="4"
                  className="textarea-custom"
                  value={formData.description}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label className="form-label-custom">
                  Special Instructions{" "}
                  <span className="text-muted fw-normal text-lowercase">
                    (optional)
                  </span>
                </label>
                <textarea
                  name="specialInstructions"
                  rows="2"
                  className="textarea-custom"
                  placeholder="e.g. Bring your ID, wear sports kit..."
                  value={formData.specialInstructions}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* === 2. Capacity === */}
          <div className="custom-card">
            <div className="card-body-custom d-flex align-items-center justify-content-between">
              <div>
                <h6 className="fw-bold m-0 d-flex align-items-center gap-2 text-dark">
                  <Users className="text-primary" size={20} /> Max Capacity
                </h6>
                <p className="text-muted small m-0 mt-1">
                  Maximum number of students allowed.
                </p>
              </div>
              <div style={{ width: "140px" }}>
                <input
                  type="number"
                  name="capacity"
                  className={`input-custom text-center ${errors.capacity ? "error-border" : ""}`}
                  value={formData.capacity}
                  onChange={handleChange}
                  min="1"
                  required
                />
                {errors.capacity && (
                  <div className="error-text">
                    <AlertTriangle size={12} /> {errors.capacity}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* === 3. Timeline === */}
          <div className="custom-card">
            <div className="card-header-custom d-flex align-items-center gap-2">
              <Calendar className="text-primary" size={20} />
              <h6 className="fw-bold m-0 text-dark">Timeline Manager</h6>
            </div>
            <div className="card-body-custom">
              {/* Registration Phase */}
              <div className="timeline-card blue">
                <div className="d-flex align-items-center gap-2 mb-3 text-primary">
                  <Clock size={16} />
                  <span className="fw-bold small text-uppercase">
                    Registration Period
                  </span>
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label-custom">Opens At</label>
                    <input
                      type="datetime-local"
                      name="registrationOpenAt"
                      className="input-custom bg-white"
                      value={formData.registrationOpenAt}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label-custom">Closes At</label>
                    <input
                      type="datetime-local"
                      name="registrationCloseAt"
                      className={`input-custom bg-white ${errors.registrationCloseAt ? "error-border" : ""}`}
                      value={formData.registrationCloseAt}
                      onChange={handleChange}
                      required
                    />
                    {errors.registrationCloseAt && (
                      <div className="error-text">
                        <AlertTriangle size={12} /> {errors.registrationCloseAt}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Activity Phase */}
              <div className="timeline-card green">
                <div className="d-flex align-items-center gap-2 mb-3 text-success">
                  <Calendar size={16} />
                  <span className="fw-bold small text-uppercase">
                    Activity Duration
                  </span>
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label-custom">Starts At</label>
                    <input
                      type="datetime-local"
                      name="activityStartAt"
                      className={`input-custom bg-white ${errors.activityStartAt ? "error-border" : ""}`}
                      value={formData.activityStartAt}
                      onChange={handleChange}
                      required
                    />
                    {errors.activityStartAt && (
                      <div className="error-text">
                        <AlertTriangle size={12} /> {errors.activityStartAt}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label-custom">Ends At</label>
                    <input
                      type="datetime-local"
                      name="activityEndAt"
                      className={`input-custom bg-white ${errors.activityEndAt ? "error-border" : ""}`}
                      value={formData.activityEndAt}
                      onChange={handleChange}
                      required
                    />
                    {errors.activityEndAt && (
                      <div className="error-text">
                        <AlertTriangle size={12} /> {errors.activityEndAt}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* === Sticky Footer === */}
          <div className="sticky-footer">
            <div
              className="container d-flex justify-content-between align-items-center"
              style={{ maxWidth: "750px" }}
            >
              <div className="d-none d-md-block">
                {isChanged ? (
                  <span className="text-dark small fw-bold d-flex align-items-center gap-2">
                    <span className="p-1 bg-warning rounded-circle d-inline-block"></span>
                    Unsaved changes
                  </span>
                ) : (
                  <span className="text-muted small">No changes made</span>
                )}
              </div>
              <div className="d-flex gap-3 ms-auto">
                <button
                  type="button"
                  className="btn btn-light fw-bold text-muted px-4"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={!isChanged}
                >
                  <Save size={18} /> Save Changes
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* === Confirmation Modal === */}
        {showConfirmModal && (
          <div className="modal-overlay-custom">
            <div className="modal-content-custom">
              <div
                className="mx-auto bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center mb-3"
                style={{ width: 60, height: 60 }}
              >
                <CheckCircle size={32} />
              </div>
              <h4 className="fw-bold text-dark mb-2">Confirm Updates?</h4>
              <p className="text-muted mb-4 small">
                Are you sure you want to save these changes? This will
                immediately update the activity details for all students.
              </p>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-light flex-grow-1 fw-bold py-2 rounded-pill"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary flex-grow-1 fw-bold py-2 rounded-pill d-flex align-items-center justify-content-center gap-2"
                  onClick={handleConfirmSave}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Saving...
                    </>
                  ) : (
                    "Yes, Save"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {popup.show && (
        <PopupMessage
          type={popup.type}
          message={popup.message}
          onClose={() => setPopup({ show: false, message: "", type: "" })}
        />
      )}
    </div>
  );
}
